import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getStatementProcessingDetails } from '../../api/cases';
import { Container, Spinner, Alert, Card } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { AxiosResponse } from 'axios';

interface StatementBoundary {
    start_index: number;
    end_index: number;
}

interface ProcessingDetails {
    raw_text: string;
    statement_boundaries: StatementBoundary[] | null;
}

const ViewStatementProcessing: React.FC = () => {
    const { case_id, file_id } = useParams<{ case_id: string; file_id: string }>();
    const { getAccessToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [details, setDetails] = useState<ProcessingDetails | null>(null);
    const statementRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const fetchAttempted = useRef(false);

    const scrollToStatement = (index: number) => {
        const element = statementRefs.current[index];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const BackButton = () => (
        <div className="mb-2">
            <Link to={`/case/${case_id}`} className="text-secondary text-decoration-none">
                ← Back to Case
            </Link>
        </div>
    );

    const fetchDetails = useCallback(async () => {
        // Only fetch once
        if (fetchAttempted.current) return;
        fetchAttempted.current = true;

        if (!case_id || !file_id) {
            setError('Missing case ID or file ID');
            setLoading(false);
            return;
        }
        
        try {
            setLoading(true);
            setError(null);
            const token = await getAccessToken();
            const response: AxiosResponse<ProcessingDetails> = await getStatementProcessingDetails(case_id, file_id, token);
            setDetails(response.data);
        } catch (err) {
            console.error('Error fetching details:', err);
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, [case_id, file_id, getAccessToken]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    const renderHighlightedText = () => {
        if (!details?.raw_text) {
            return <Alert variant="info">No text content available.</Alert>;
        }

        if (!details.statement_boundaries?.length) {
            // If no boundaries, just show the raw text
            return (
                <div>
                    <Alert variant="info" className="mb-3">
                        No statement boundaries found. Showing raw text:
                    </Alert>
                    {details.raw_text}
                </div>
            );
        }

        const text = details.raw_text;
        const result: JSX.Element[] = [];
        let lastIndex = 0;

        // Sort boundaries by start_index to ensure proper order
        const sortedBoundaries = [...details.statement_boundaries].sort((a, b) => a.start_index - b.start_index);

        // Add any text before first statement
        if (sortedBoundaries[0].start_index > 0) {
            result.push(
                <div key="pre-content" className="text-muted mb-3 pb-3 border-bottom">
                    <strong>Pre-statement content:</strong><br />
                    {text.slice(0, sortedBoundaries[0].start_index)}
                </div>
            );
        }

        // Add each statement with its surrounding text
        sortedBoundaries.forEach((boundary, index) => {
            // Add any text between statements
            if (boundary.start_index > lastIndex && lastIndex > 0) {
                result.push(
                    <div key={`gap-${index}`} className="text-muted mb-3 pb-3 border-bottom">
                        <strong>Content between statements {index} and {index + 1}:</strong><br />
                        {text.slice(lastIndex, boundary.start_index)}
                    </div>
                );
            }

            // Add the statement text with highlighting
            const backgroundColor = `hsl(${(index * 60) % 360}, 70%, 90%)`;
            result.push(
                <div
                    key={`statement-${index}`}
                    ref={el => statementRefs.current[index] = el}
                    className="mb-4 pb-3 border-bottom"
                    style={{ backgroundColor }}
                    id={`statement-${index}`}
                >
                    <div className="d-flex justify-content-between align-items-center mb-2 pt-2 px-2">
                        <strong>Statement {index + 1}</strong>
                        <small className="text-secondary">
                            Characters {boundary.start_index} to {boundary.end_index}
                            ({boundary.end_index - boundary.start_index} chars)
                        </small>
                    </div>
                    <div className="px-2 pb-2" style={{ whiteSpace: 'pre-wrap' }}>
                        {text.slice(boundary.start_index, boundary.end_index)}
                    </div>
                </div>
            );

            lastIndex = boundary.end_index;
        });

        // Add any remaining text after the last statement
        if (lastIndex < text.length) {
            result.push(
                <div key="post-content" className="text-muted mt-3">
                    <strong>Post-statement content:</strong><br />
                    {text.slice(lastIndex)}
                </div>
            );
        }

        return result;
    };

    if (loading) {
        return (
            <Container>
                <BackButton />
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <BackButton />
                <div className="py-4">
                    <Alert variant="danger">
                        <Alert.Heading>Error Loading Processing Details</Alert.Heading>
                        <p>{error}</p>
                    </Alert>
                </div>
            </Container>
        );
    }

    if (!details?.raw_text) {
        return (
            <Container>
                <BackButton />
                <div className="py-4">
                    <Alert variant="warning">No processing details available.</Alert>
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <BackButton />
            <div className="py-4">
                <Card>
                    <Card.Header>
                        <h4 className="mb-0">Bank Statement Processing Details</h4>
                    </Card.Header>
                    <Card.Body>
                        <div className="mb-4">
                            <h5>Statement Boundaries</h5>
                            <div className="d-flex flex-wrap gap-2">
                                {details?.statement_boundaries?.length ? (
                                    details.statement_boundaries.map((boundary, index) => (
                                        <div 
                                            key={index} 
                                            className="px-3 py-2 rounded" 
                                            style={{ 
                                                backgroundColor: `hsl(${(index * 60) % 360}, 70%, 90%)`,
                                                fontSize: '0.9em',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => scrollToStatement(index)}
                                            role="button"
                                            title="Click to scroll to this statement"
                                        >
                                            Statement {index + 1}: Characters {boundary.start_index} to {boundary.end_index}
                                            <br />
                                            <span className="text-secondary">
                                                ({boundary.end_index - boundary.start_index} characters)
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <Alert variant="info">No statement boundaries found.</Alert>
                                )}
                            </div>
                        </div>

                        <h5>Raw Text {!details?.statement_boundaries?.length && '(No Boundaries Found)'}</h5>
                        <div 
                            className="p-3 mt-2 bg-white rounded shadow-sm" 
                            style={{ 
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                maxHeight: '600px',
                                overflow: 'auto'
                            }}
                        >
                            {renderHighlightedText()}
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </Container>
    );
};

export default ViewStatementProcessing; 